import { z } from 'zod';
import { CREDIT_NOTE_STATUSES, PAYMENT_METHODS } from '../enums.js';
import {
  optionalDate,
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import { currencySchema } from './organization.js';
import { DOCUMENT_CURRENCIES, lineItemSchema, type LineItemView } from './sales-documents.js';
import { businessManagerFilterSchema, type BusinessManagerRef, type RecordRef } from './records.js';

/**
 * Credit Notes.
 *
 * Built on the same document shape as `Quote`/`Invoice` — line items, a
 * frozen currency/exchange-rate snapshot, Terms & Conditions stamped once at
 * creation — reusing `lineItemSchema` and the totals engine
 * (`computeDocumentTotals`) directly rather than a second implementation of
 * either.
 *
 * What a credit note has that those do not is a running "how much of this
 * has been applied" figure (`appliedAmount`/`remainingAmount`), which is
 * computed from `CreditNoteApplication` rows, never stored as a mutable
 * column — the same separation `Invoice` keeps from `PaymentReceived`.
 */

const money = z.coerce.number().min(0).max(1e15);

const lines = z.array(lineItemSchema).max(200);

export const createCreditNoteSchema = z.object({
  /** Required: a credit note is addressed to somebody, the same reasoning `createQuoteSchema.accountId` documents. */
  accountId: uuidSchema.describe('Required: a credit note is addressed to somebody'),
  contactId: uuidSchema.nullish(),
  /**
   * The invoice this credits, if any. Fixed once set — the same reasoning
   * `updatePaymentSchema` keeps `invoiceId` out of: moving a credit note to a
   * different invoice would restate two balances at once, and the honest
   * correction is to cancel it and raise it again where it belongs.
   */
  invoiceId: uuidSchema.describe('Required: the invoice this credit note is for'),
  creditNoteDate: z.iso.date(),
  referenceDate: optionalDate(),
  reason: optionalText(300),
  notes: optionalText(8000),
  terms: optionalText(8000),
  templateId: uuidSchema.nullish(),
  businessManagerId: uuidSchema.optional(),
  lineItems: lines.optional(),
  /** Same shortlist `createQuoteSchema.currency` offers; any ISO code is still accepted. */
  currency: z.enum(DOCUMENT_CURRENCIES).optional(),
});
export type CreateCreditNoteInput = z.infer<typeof createCreditNoteSchema>;

export const updateCreditNoteSchema = toPatchSchema(
  createCreditNoteSchema.omit({ invoiceId: true }),
).extend({
  currency: currencySchema.optional(),
});
export type UpdateCreditNoteInput = z.infer<typeof updateCreditNoteSchema>;

export const applyCreditNoteSchema = z.object({
  amount: money.refine((value) => value > 0, 'Enter an amount greater than zero'),
  appliedOn: z.iso.date(),
});
export type ApplyCreditNoteInput = z.infer<typeof applyCreditNoteSchema>;

export const unapplyCreditNoteSchema = z.object({
  applicationId: uuidSchema,
});
export type UnapplyCreditNoteInput = z.infer<typeof unapplyCreditNoteSchema>;

/**
 * Returning cash to the customer against a credit note.
 *
 * Distinct from applying it: applying lowers what an invoice still owes; a
 * refund hands money back, and is the only way to use a credit once the invoice
 * it relates to has been paid in full. Posts a bank transaction when a bank
 * account is named.
 */
export const refundCreditNoteSchema = z.object({
  amount: money.refine((value) => value > 0, 'Enter an amount greater than zero'),
  refundedOn: z.iso.date(),
  method: z.enum(PAYMENT_METHODS).default('BANK_TRANSFER'),
  reference: optionalText(120),
  notes: optionalText(2000),
  /** The account the money leaves; its currency must match the credit note's. Required, so every refund shows as money out in banking. */
  bankAccountId: uuidSchema,
});
export type RefundCreditNoteInput = z.infer<typeof refundCreditNoteSchema>;

export const unrefundCreditNoteSchema = z.object({
  refundId: uuidSchema,
});
export type UnrefundCreditNoteInput = z.infer<typeof unrefundCreditNoteSchema>;

/**
 * Raising a credit note from an invoice: the customer, currency, exchange-rate
 * snapshot and lines come from the invoice, so a credit reverses exactly what
 * was billed.
 *
 *  - `FULL`   — every line, as invoiced.
 *  - `LINES`  — chosen lines, each with a quantity up to what was invoiced.
 *  - `AMOUNT` — a single credit for a stated amount (a goodwill adjustment).
 */
export const createCreditNoteFromInvoiceSchema = z
  .object({
    mode: z.enum(['FULL', 'LINES', 'AMOUNT']).default('FULL'),
    creditNoteDate: z.iso.date(),
    reason: optionalText(300),
    notes: optionalText(8000),
    lines: z
      .array(
        z.object({
          invoiceLineItemId: uuidSchema,
          quantity: z.coerce.number().int().min(1).max(1_000_000),
        }),
      )
      .max(200)
      .optional(),
    amount: money.optional(),
  })
  .refine((value) => value.mode !== 'LINES' || (value.lines?.length ?? 0) > 0, {
    message: 'Choose at least one line to credit',
    path: ['lines'],
  })
  .refine((value) => value.mode !== 'AMOUNT' || (value.amount ?? 0) > 0, {
    message: 'Enter the amount to credit',
    path: ['amount'],
  });
export type CreateCreditNoteFromInvoiceInput = z.infer<typeof createCreditNoteFromInvoiceSchema>;

export const CREDIT_NOTE_SORT_FIELDS = [
  'createdAt',
  'updatedAt',
  'total',
  'status',
  'creditNoteDate',
] as const;

export const listCreditNotesQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(CREDIT_NOTE_SORT_FIELDS).default('createdAt'),
  businessManagerId: businessManagerFilterSchema.optional(),
  accountId: uuidSchema.optional(),
  invoiceId: uuidSchema.optional(),
  status: z.enum(CREDIT_NOTE_STATUSES).optional(),
  /** Issued or partly applied: credit with value still to use. */
  openOnly: z.stringbool().optional(),
});
export type ListCreditNotesQuery = z.infer<typeof listCreditNotesQuerySchema>;

export interface CreditNoteApplicationView {
  id: string;
  /** Negative when this row is a reversal ("unapply") of an earlier one. */
  amount: string;
  appliedOn: string;
  reversalOfId: string | null;
  createdAt: string;
}

export interface CreditNoteRefundView {
  id: string;
  /** Negative when this row reverses an earlier refund. */
  amount: string;
  refundedOn: string;
  method: string;
  reference: string | null;
  notes: string | null;
  bankAccount: { id: string; name: string } | null;
  /** The bank transaction the refund posted, when a bank account was named. */
  bankTransactionId: string | null;
  reversalOfId: string | null;
  createdAt: string;
}

export interface CreditNoteSummary {
  id: string;
  /** The allocated document number: CN-00007. */
  number: string;
  status: (typeof CREDIT_NOTE_STATUSES)[number];
  creditNoteDate: string;
  referenceDate: string | null;
  reason: string | null;
  currency: string;
  baseCurrency: string;
  exchangeRate: string;
  exchangeRateAt: string;
  exchangeRateSource: string;
  baseTotal: string;
  subtotal: string;
  discountAmount: string;
  taxAmount: string;
  total: string;
  /** Sum of live (non-reversed) applications against `invoice`. */
  appliedAmount: string;
  /** Cash returned to the customer, net of reversed refunds. */
  refundedAmount: string;
  /** `total - appliedAmount - refundedAmount`, floored at zero: what can still be applied or refunded. */
  remainingAmount: string;
  account: RecordRef | null;
  contact: RecordRef | null;
  invoice: RecordRef | null;
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreditNoteDetail extends CreditNoteSummary {
  notes: string | null;
  terms: string | null;
  templateId: string | null;
  lineItems: LineItemView[];
  applications: CreditNoteApplicationView[];
  refunds: CreditNoteRefundView[];
}
